import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, ArrowLeft, Calendar } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function TimeOff() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tech, setTech] = useState(null);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    reason: ""
  });

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchTech();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (tech) {
      fetchTimeOff();
    }
  }, [tech]);

  const fetchTech = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/techs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const myTech = response.data.find(t => t.email === user.email);
      if (!myTech) {
        console.error("No tech found for user email:", user.email);
        toast.error("Tech profile not found. Please contact admin.");
        return;
      }
      setTech(myTech);
    } catch (error) {
      console.error("Failed to fetch tech:", error);
      toast.error("Failed to fetch tech info: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchTimeOff = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const params = user.role === "Admin" ? {} : { tech_id: tech?.tech_id };
      const response = await axios.get(`${API}/time-off`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTimeOffRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch time off:", error);
      toast.error("Failed to fetch time off requests: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!tech) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/time-off`, {
        tech_id: tech.tech_id,
        ...formData
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Time off request submitted");
      setShowAddForm(false);
      setFormData({
        start_date: "",
        end_date: "",
        reason: ""
      });
      fetchTimeOff();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/time-off/${deleteId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Request deleted");
      setDeleteId(null);
      fetchTimeOff();
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };

  const handleApprove = async (timeoffId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/time-off/${timeoffId}`, {
        status: "approved"
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Request approved");
      fetchTimeOff();
    } catch (error) {
      toast.error("Failed to approve request");
    }
  };

  const handleDeny = async (timeoffId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/time-off/${timeoffId}`, {
        status: "denied"
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Request denied");
      fetchTimeOff();
    } catch (error) {
      toast.error("Failed to deny request");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "denied":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      default:
        return "bg-zinc-100 text-zinc-800 border-zinc-200";
    }
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className="max-w-md mx-auto pb-24 p-4">
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-inter font-semibold">Back to Settings</span>
          </button>
          
          <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase mb-2">Time Off</h1>
          <p className="font-inter text-zinc-600">
            {user?.role === "Admin" ? "Manage all time off requests" : "Request time off"}
          </p>
        </div>

        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            data-testid="add-timeoff-button"
            className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold flex items-center justify-center gap-2 mb-6"
          >
            <Plus className="w-5 h-5" />
            Request Time Off
          </button>
        ) : (
          <form onSubmit={handleAdd} className="bg-white rounded-xl border-2 border-zinc-100 p-5 mb-6">
            <h3 className="font-inter text-lg font-bold text-zinc-900 mb-4">New Time Off Request</h3>
            
            <div className="space-y-4">
              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                />
              </div>

              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                  className="w-full h-12 rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base px-4 bg-white"
                />
              </div>

              <div>
                <label className="font-inter font-semibold text-zinc-700 block mb-2">Reason (Optional)</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border-2 border-zinc-200 focus:border-[#207024] text-base p-4 bg-white"
                  placeholder="e.g., Family vacation, Personal day..."
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="h-12 flex-1 rounded-lg bg-white border-2 border-zinc-200 text-zinc-900 font-semibold hover:bg-zinc-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-12 flex-1 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-semibold"
                >
                  Submit Request
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="space-y-4">
          {timeOffRequests.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="font-inter text-zinc-500">No time off requests</p>
            </div>
          ) : (
            timeOffRequests.map((request) => (
              <div
                key={request.timeoff_id}
                className="bg-white rounded-xl border-2 border-zinc-100 p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    {user?.role === "Admin" && (
                      <p className="font-inter text-sm font-semibold text-[#207024] mb-1">
                        {request.tech_name}
                      </p>
                    )}
                    <h3 className="font-inter text-lg font-bold text-zinc-900">
                      {new Date(request.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(request.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </h3>
                    {request.reason && (
                      <p className="text-sm text-zinc-600 mt-1 italic">{request.reason}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${getStatusColor(request.status)}`}>
                    {request.status}
                  </span>
                </div>

                <div className="flex gap-2">
                  {user?.role === "Admin" && request.status === "pending" && (
                    <>
                      <button
                        onClick={() => handleApprove(request.timeoff_id)}
                        className="h-10 flex-1 rounded-lg bg-green-50 border-2 border-green-200 text-green-700 font-semibold hover:bg-green-100"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDeny(request.timeoff_id)}
                        className="h-10 flex-1 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 font-semibold hover:bg-red-100"
                      >
                        Deny
                      </button>
                    </>
                  )}
                  
                  {(user?.role === "Admin" || request.status === "pending") && (
                    <button
                      onClick={() => setDeleteId(request.timeoff_id)}
                      className="h-10 px-4 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 font-semibold hover:bg-red-100 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this time off request? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
